import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhotoThumb from '../PhotoThumb';
import { newBatch, newBatchAudio, newBatchPhoto } from '../../utils/fieldBatches';
import { thumbnailOf } from '../../utils/fieldFrame';
import { localIsoWithOffset } from '../../utils/specimenCode';
import { exifFromFile } from '../../utils/exifGps';
import { isValidCoordinate } from '../../utils/geo';
import { can } from '../../utils/permissions';
import { isReadOnlyRefusal } from '../../utils/viewAs';
import MapGpsPicker from '../MapGpsPicker';
import VoiceNoteRecorder from '../VoiceNoteRecorder';
import FieldDetailsForm from '../field/FieldDetailsForm';
import AiFieldSuggestion from '../field/AiFieldSuggestion';
import { tagBlocks, useTagCheck } from '../field/useTagCheck';
import { hintStyle, sectionHeading } from '../intake/styles';
import { OBSERVATION, readFieldMode } from '../../utils/fieldMode';
import RareFindPrompt from '../handoffs/RareFindPrompt';
import { rareFindOf } from '../../utils/handoffs';

/*
 * A field collection from Home ("Add isolate / dish" -> Field collection):
 * what Field Mode's camera and editor record, on one form at a desk or on a
 * phone -- where and when, the photographs, the identification and what it
 * grew on, notes said or typed, and whether anything was collected.
 *
 * Saved the way Field Mode saves and the national map's ObservationWizard
 * does (utils/fieldBatches.js): a batch on THIS DEVICE first, its photos and
 * voice notes beside it, then the outbox (utils/fieldOutbox.js) sends it --
 * at once with a connection, otherwise when one returns. So minting, its
 * idempotency, the retries and "needs attention" are Field Mode's own, and
 * nothing here re-implements them:
 *
 *   * a collection is a batch the outbox mints a specimen code for (where
 *     the role may: specimen:register) unless a bag label is given;
 *   * "sighting only" is details.observeOnly: no code is minted.
 *
 * Every photograph and voice note is optional (CLAUDE.md, "Specimen photos
 * are optional"). A place is required: the outbox mints no code for a
 * specimen with no place, and a hub form has no camera to find one later. A
 * photograph whose EXIF says where it was taken places the specimen there
 * if no place is set yet.
 *
 * Props:
 *   user, store, outbox   the signed-in user, the device store and the outbox
 *   onSaved(batch, { queued })  saved on this device; queued: no connection now
 *   onCancel              closes without saving
 *   onBusyChange(busy)    a save started (true) or ended (false)
 */
export const SLOTS = [
  { key: 'top', view: 'cap_top' },
  { key: 'bottom', view: 'fertile_layer' },
  { key: 'habitat', view: 'habitat' },
];

const toLocalInput = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const online = () => typeof navigator === 'undefined' || navigator.onLine !== false;

export default function FieldIntakeForm({ user, store, outbox, onSaved, onCancel, onBusyChange }) {
  const { t } = useTranslation(['isolates', 'field', 'common', 'viewAs']);
  const userId = user?.user_id;
  const canMint = can(user, 'specimen:register');
  // Starts as Field Mode's switch stands on this device (utils/fieldMode.js).
  const [mode, setMode] = useState(() => (readFieldMode(userId, canMint) === OBSERVATION ? 'sighting' : 'collection'));
  const [when, setWhen] = useState(() => toLocalInput(new Date()));
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [fromPhoto, setFromPhoto] = useState(false);
  const [details, setDetails] = useState({});
  const [notes, setNotes] = useState('');
  const [audio, setAudio] = useState([]);
  // The AI's answer, asked beside the photographs (AiFieldSuggestion): kept on
  // the batch, linked to the record by the outbox once it exists.
  const [ai, setAi] = useState(null);
  // The rare-find prompt (B6): a High-certainty answer naming a rare species
  // offers a pickup or a drop-off, kept with the batch the form saves.
  const [rareFind, setRareFind] = useState(null);
  const [handoff, setHandoff] = useState(null);
  const askedRarity = useRef(null);
  const heard = (result) => {
    if (!result?.ai_id || askedRarity.current === result.ai_id) return;
    askedRarity.current = result.ai_id;
    rareFindOf(result, user).then((found) => {
      if (askedRarity.current === result.ai_id) setRareFind(found);
    });
  };
  // slot key -> { file, url, gps, takenAt }; extras: [{ id, file, url, gps, takenAt }]
  const [photos, setPhotos] = useState({});
  const [extras, setExtras] = useState([]);
  const [reading, setReading] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const tag = useTagCheck({ status: 'idle' }, () => !!outbox?.signedIn?.());
  const urls = useRef([]);
  const place = useRef({ lat, lng });
  place.current = { lat, lng };

  useEffect(() => {
    const made = urls.current;
    return () => made.forEach((u) => URL.revokeObjectURL?.(u));
  }, []);

  const busy = (value) => {
    setSaving(value);
    onBusyChange?.(value);
  };

  const preview = (file) => {
    if (typeof URL.createObjectURL !== 'function') return '';
    const url = URL.createObjectURL(file);
    urls.current.push(url);
    return url;
  };

  /* A picked or captured photograph: its EXIF read for when and where it
   * was taken, and the first that knows where places the specimen if
   * nothing has yet. */
  const read = async (file) => {
    setReading((n) => n + 1);
    const meta = await exifFromFile(file).catch(() => null);
    setReading((n) => n - 1);
    const gps = meta?.gps || null;
    if (gps && String(place.current.lat).trim() === '' && String(place.current.lng).trim() === '') {
      setLat(String(gps.lat));
      setLng(String(gps.lng));
      setFromPhoto(true);
    }
    return { file, url: preview(file), gps, takenAt: meta?.takenAt || null };
  };

  const chooseSlot = async (key, file) => {
    if (!file) return;
    const chosen = await read(file);
    setPhotos((all) => ({ ...all, [key]: chosen }));
  };

  const addExtras = async (files) => {
    for (const file of files) {
      const chosen = await read(file);
      setExtras((list) => [...list, { ...chosen, id: `${Date.now()}-${list.length}` }]);
    }
  };

  const labelBlocked = tagBlocks(tag.tagState, details.label);

  const save = async () => {
    setError('');
    const moment = new Date(when);
    if (!when || Number.isNaN(moment.getTime())) return setError(t('fieldIntake.whenRequired'));
    if (moment.getTime() > Date.now() + 60 * 1000) return setError(t('fieldIntake.whenFuture'));
    if (String(lat).trim() === '' || String(lng).trim() === '') return setError(t('fieldIntake.whereRequired'));
    if (!isValidCoordinate(lat, lng)) return setError(t('fieldIntake.whereInvalid'));
    if (!userId || !store) return setError(t('fieldIntake.storeFailed'));
    busy(true);
    try {
      const batchPhotos = [];
      const chosen = [
        ...SLOTS.filter((s) => photos[s.key]).map((s) => ({ ...photos[s.key], view: s.view })),
        ...extras.map((x, i) => ({ ...x, view: 'other', note: t('field:camera.extraNote', { n: i + 1 }) })),
      ];
      for (const p of chosen) {
        const photo = newBatchPhoto({ view: p.view, takenAt: p.takenAt, fix: p.gps, note: p.note });
        await store.putBlob(userId, photo.id, p.file);
        batchPhotos.push(photo);
      }
      const audioNotes = [];
      for (const note of audio) {
        const entry = newBatchAudio({ contentType: note.contentType, durationS: note.durationS,
          recordedAt: note.recordedAt });
        await store.putBlob(userId, entry.id, note.blob);
        audioNotes.push(entry);
      }
      // A card picture only: failing to make one must not cost the record.
      const cover = chosen[0];
      const thumbnail = cover ? await Promise.resolve().then(() => thumbnailOf(cover.file)).catch(() => null) : null;
      const batch = await store.save({
        ...newBatch({ userId, source: 'hub' }),
        status: 'pending',
        startedAt: localIsoWithOffset(moment),
        location: { lat: Number(lat), lng: Number(lng), accuracy_m: null },
        photos: batchPhotos,
        audioNotes,
        ...(ai?.aiId ? { ai } : {}),
        ...(handoff ? { handoff: { ...handoff.request, aiId: handoff.aiId, askedAt: new Date().toISOString() },
          handoffSent: false } : {}),
        details: {
          ...details,
          observeOnly: mode === 'sighting',
          general_notes: notes.trim() || undefined,
        },
        thumbnail,
        finalizedAt: new Date().toISOString(),
      });
      store.ensureDurable?.().catch(() => {});
      // Started, not only run: Home may be the first screen of this page
      // to use the outbox, and it should keep listening for a connection.
      if (typeof outbox?.start === 'function') outbox.start();
      else outbox?.run?.();
      busy(false);
      onSaved?.(batch, { queued: !online() });
    } catch (e) {
      busy(false);
      setError(isReadOnlyRefusal(e) ? t('viewAs:notKept') : t('fieldIntake.storeFailed'));
    }
    return undefined;
  };

  const disabled = saving;
  return (
    <div data-testid="field-intake-form" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Collection or sighting only, as Field Mode asks it. */}
      <fieldset style={box} disabled={disabled} data-testid="field-intake-mode">
        <legend style={legend}>{t('fieldIntake.modeLegend')}</legend>
        <div role="radiogroup" aria-label={t('fieldIntake.modeLegend')} style={row}>
          {['collection', 'sighting'].map((m) => (
            <label key={m} style={pill(mode === m)} title={t(`fieldIntake.tip.${m}`)}>
              <input type="radio" name="field-intake-mode" value={m} checked={mode === m}
                onChange={() => setMode(m)} data-testid={`field-intake-mode-${m}`} />
              {t(`fieldIntake.mode.${m}`)}
            </label>
          ))}
        </div>
        <p style={hintStyle}>
          {mode === 'sighting' ? t('fieldIntake.sightingHint')
            : canMint ? t('fieldIntake.collectionHint') : t('fieldIntake.collectionNoMintHint')}
        </p>
      </fieldset>

      <fieldset style={box} disabled={disabled} data-testid="field-intake-where">
        <legend style={legend}>{t('fieldIntake.whenWhere')}</legend>
        <label style={field}>
          <span style={label}>{t('fieldIntake.when')}</span>
          <input type="datetime-local" value={when} max={toLocalInput(new Date())} style={input}
            onChange={(e) => setWhen(e.target.value)} data-testid="field-intake-when" />
        </label>
        {fromPhoto && <p role="status" style={hintStyle} data-testid="field-intake-from-photo">{t('fieldIntake.fromPhoto')}</p>}
        <MapGpsPicker lat={lat} lng={lng} hintText={t('fieldIntake.whereHint')}
          onChange={(a, b) => { setLat(a); setLng(b); setFromPhoto(false); }} />
      </fieldset>

      <fieldset style={box} disabled={disabled} data-testid="field-intake-photos">
        <legend style={legend}>{t('fieldIntake.photos')}</legend>
        <p style={hintStyle}>{t('fieldIntake.photosHint')}</p>
        <div style={grid}>
          {SLOTS.map((s) => (
            <PhotoSlot key={s.key} slot={s} chosen={photos[s.key]} t={t}
              onPick={(file) => chooseSlot(s.key, file)}
              onRemove={() => setPhotos((all) => { const next = { ...all }; delete next[s.key]; return next; })} />
          ))}
        </div>
        {extras.length > 0 && (
          <ul style={extrasList}>
            {extras.map((x, i) => (
              <li key={x.id} style={extraItem} data-testid={`field-intake-extra-${i}`}>
                {x.url && <img src={x.url} alt={t('fieldIntake.extraAlt', { n: i + 1 })} style={thumb} />}
                <button type="button" style={smallBtn} aria-label={t('fieldIntake.removeExtra', { n: i + 1 })}
                  onClick={() => setExtras((list) => list.filter((y) => y.id !== x.id))}>×</button>
              </li>
            ))}
          </ul>
        )}
        <label style={{ ...secondaryBtn, alignSelf: 'flex-start' }} title={t('fieldIntake.tip.addExtras')}>
          {t('fieldIntake.addExtras')}
          <input type="file" accept="image/*" multiple style={{ display: 'none' }} data-testid="field-intake-extras"
            onChange={(e) => { const files = [...(e.target.files || [])]; e.target.value = ''; addExtras(files); }} />
        </label>
        {/* Identify with AI, beside the photographs it looks at; a name it
            gives is filled into the details below, for the person to check. */}
        <AiFieldSuggestion user={user} disabled={disabled} value={ai} onChange={setAi}
          hadOwnName={!!details.taxon_name} testid="field-intake-ai"
          photos={[...SLOTS.filter((s) => photos[s.key]).map((s) => ({ file: photos[s.key].file, view: s.view })),
            ...extras.map((x) => ({ file: x.file, view: 'other' }))]}
          onResult={heard}
          onAccept={(chosen) => setDetails((d) => ({ ...d, taxon_name: chosen.taxon_name, rank: chosen.rank,
            confidence: d.confidence || 'uncertain' }))} />
        {/* No "Meet where I am now": the place here is the find's own (a map
            pin, a photograph's EXIF), not where the person stands, and a
            meeting point is shown to the lab in place of the site. */}
        {(rareFind || handoff) && (
          <RareFindPrompt find={rareFind} request={handoff?.request || null} disabled={disabled} location={null}
            mayCollect={mode === 'sighting' && canMint} onCollect={async () => setMode('collection')}
            onRequest={async (request, aiId) => setHandoff({ request, aiId })}
            onRemove={async () => setHandoff(null)} testId="field-intake-rare" />
        )}
      </fieldset>

      {/* Who it is, what it grew on, a bag label -- Field Mode's own details.
          Notes and "sighting only" are this form's, above and below. */}
      <FieldDetailsForm value={details} onChange={setDetails} canMint={canMint} tag={tag} disabled={disabled}
        canAsk={() => !!outbox?.signedIn?.()} showNotes={false} showObserveOnly={false} />

      <fieldset style={box} disabled={disabled} data-testid="field-intake-notes">
        <legend style={legend}>{t('fieldIntake.notesTitle')}</legend>
        <label style={field}>
          <span style={hintStyle}>{t('fieldIntake.notesHint')}</span>
          <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...input, resize: 'vertical' }}
            data-testid="field-intake-notes-text" />
        </label>
        <VoiceNoteRecorder value={audio} onChange={setAudio} disabled={disabled} testId="field-intake-voice" />
      </fieldset>

      {error && <p role="alert" style={{ margin: 0, color: 'var(--c-danger-fg)', fontSize: 14 }}
        data-testid="field-intake-error">{error}</p>}
      {!online() && <p style={hintStyle} data-testid="field-intake-offline">{t('fieldIntake.offline')}</p>}
      {labelBlocked && <p style={hintStyle}>{t('field:details.labelBlocked')}</p>}
      <div style={row}>
        <button type="button" onClick={save} disabled={saving || reading > 0 || labelBlocked} style={primaryBtn}
          data-testid="field-intake-save" title={t('fieldIntake.tip.save')}>
          {saving ? t('common:saving') : t('fieldIntake.save')}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={saving} style={secondaryBtn}
            data-testid="field-intake-cancel">
            {t('common:cancel')}
          </button>
        )}
      </div>
    </div>
  );
}

/* One of the three guided photographs: take one (the camera, on a phone) or
 * choose one from the device; shown once chosen, with a way to remove it. */
function PhotoSlot({ slot, chosen, onPick, onRemove, t }) {
  const camera = useRef(null);
  const upload = useRef(null);
  const pick = (e) => { const file = e.target.files?.[0]; e.target.value = ''; onPick(file); };
  return (
    <div style={slotBox} data-testid={`field-intake-slot-${slot.key}`}>
      <span style={label}>{t(`fieldIntake.slot.${slot.key}`)}</span>
      {chosen?.url
        ? (
          <PhotoThumb photos={[{ s3_url: chosen.url, notes: t(`fieldIntake.slot.${slot.key}`) }]}
            testId={`field-intake-open-${slot.key}`} dialogLabel={t(`fieldIntake.slot.${slot.key}`)}>
            <img src={chosen.url} alt={t(`fieldIntake.slot.${slot.key}`)} style={slotImg} />
          </PhotoThumb>
        )
        : <div style={slotEmpty} aria-hidden="true">📷</div>}
      <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={pick}
        data-testid={`field-intake-take-${slot.key}`} />
      <input ref={upload} type="file" accept="image/*" hidden onChange={pick}
        data-testid={`field-intake-choose-${slot.key}`} />
      <div style={row}>
        <button type="button" style={smallBtn} onClick={() => camera.current?.click()}>
          {chosen ? t('fieldIntake.retake') : t('fieldIntake.take')}
        </button>
        <button type="button" style={smallBtn} onClick={() => upload.current?.click()}>{t('fieldIntake.choose')}</button>
        {chosen && <button type="button" style={smallBtn} onClick={onRemove}>{t('fieldIntake.remove')}</button>}
      </div>
    </div>
  );
}

const box = { display: 'flex', flexDirection: 'column', gap: 10, padding: 14, margin: 0, minWidth: 0,
  border: '1px solid var(--c-border)', borderRadius: 12, background: 'var(--c-surface)' };
const legend = { ...sectionHeading, padding: '0 4px', margin: 0 };
const row = { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' };
const field = { display: 'flex', flexDirection: 'column', gap: 6 };
const label = { fontSize: 13, fontWeight: 600, color: 'var(--c-text-fg)' };
const input = { fontSize: 15, padding: '10px 12px', border: '1px solid var(--c-border-strong)', borderRadius: 8,
  background: 'var(--c-surface)', color: 'var(--c-text-fg)', font: 'inherit', minHeight: 44, boxSizing: 'border-box',
  width: '100%' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: 10 };
const slotBox = { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 };
const slotImg = { width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, display: 'block' };
const slotEmpty = { height: 120, borderRadius: 8, border: '2px dashed var(--c-border)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', color: 'var(--c-text-muted-fg)', fontSize: 22 };
const extrasList = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 8 };
const extraItem = { display: 'flex', alignItems: 'center', gap: 4 };
const thumb = { width: 56, height: 56, objectFit: 'cover', borderRadius: 6 };
const pill = (on) => ({ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 999,
  cursor: 'pointer', minHeight: 44, boxSizing: 'border-box', fontSize: 14, fontWeight: on ? 700 : 500,
  color: 'var(--c-text-fg)', background: on ? 'var(--c-primary-soft)' : 'var(--c-surface)',
  border: `1px solid ${on ? 'var(--c-primary)' : 'var(--c-border-strong)'}` });
const primaryBtn = { minHeight: 48, padding: '0 20px', border: 'none', borderRadius: 10, background: 'var(--c-primary)',
  color: 'var(--c-on-accent)', font: 'inherit', fontSize: 16, fontWeight: 600, cursor: 'pointer' };
const secondaryBtn = { display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 16px',
  border: '1px solid var(--c-border-strong)', borderRadius: 10, background: 'var(--c-surface)', color: 'var(--c-text-fg)',
  font: 'inherit', fontSize: 15, cursor: 'pointer' };
const smallBtn = { minHeight: 40, minWidth: 40, padding: '0 10px', border: '1px solid var(--c-border-strong)',
  borderRadius: 8, background: 'var(--c-surface)', color: 'var(--c-text-fg)', font: 'inherit', fontSize: 13,
  cursor: 'pointer' };
